import { Row } from "reactstrap";
import React from "react";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { useProblemModelMap } from "../../api/APIClient";
import Contest from "../../interfaces/Contest";
import MergedProblem from "../../interfaces/MergedProblem";
import {
  contestProblemKey,
  noneStatus,
  ProblemStatus,
  StatusLabel,
} from "../../interfaces/Status";
import {
  ColorMode,
  TableColor,
  statusToTableColor,
  combineTableColorList,
} from "../../utils/TableColor";
import { ProblemLink } from "../../components/ProblemLink";
import { ContestLink } from "../../components/ContestLink";
import ProblemModel from "../../interfaces/ProblemModel";
import { SubmitTimespan } from "../../components/SubmitTimespan";
import { RatingInfo } from "../../utils/RatingInfo";
import { ProblemPoint } from "../../components/Problempoint";
import { classifyContest } from "../../utils/ContestClassifier";

interface Props {
  contests: Contest[];
  contestToProblems: Map<string, MergedProblem[]>;
  showOnlyAttemptedContest: boolean;
  hideCompleteContests: boolean;
  showDifficulty: boolean;
  colorMode: ColorMode;
  title: string;
  statusLabelMap: Map<string, ProblemStatus>;
  showPenalties: boolean;
  selectedLanguages: Set<string>;
  userRatingInfo: RatingInfo;
}

const getProblemHeaderAlphabet = (problem: MergedProblem, contest: Contest) => {
  if (
    problem.problem_index === "H" &&
    classifyContest(contest).startsWith("ABC")
  ) {
    return "H";
  }
  if (problem.problem_index === "Ex") {
    return "H";
  }
  if (problem.problem_index === "F2") {
    return "G";
  }
  return problem.problem_index;
};

const AtCoderRegularTableSFC: React.FC<Props> = (props) => {
  const { colorMode, selectedLanguages, showPenalties, userRatingInfo } = props;
  const problemModels = useProblemModelMap();
  interface CellEntry {
    problem: MergedProblem;
    status: ProblemStatus;
    model?: ProblemModel;
    cellColor: TableColor;
    duplicates: MergedProblem[];
  }
  interface OneContest {
    contest: Contest;
    id: string;
    problemStatus: Map<string, CellEntry>;
    attempted: boolean;
    rowColor: TableColor;
  }
  const contests: OneContest[] = props.contests
    .map((contest) => {
      const problems = (
        props.contestToProblems.get(contest.id) ?? []
      ).sort((a, b) => a.id.localeCompare(b.id));
      const problemStatusList = problems.map((problem) => {
        const status =
          props.statusLabelMap.get(contestProblemKey(contest.id, problem.id)) ??
          noneStatus();
        return {
          problem,
          status,
          model: problemModels?.get(problem.id),
          cellColor: statusToTableColor({
            colorMode,
            status,
            contest,
            selectedLanguages,
          }),
          duplicates: [] as MergedProblem[],
        };
      });
      const problemStatus = new Map<string, CellEntry>();
      problemStatusList.forEach((entry) => {
        const alphabet = getProblemHeaderAlphabet(entry.problem, contest);
        const existing = problemStatus.get(alphabet);
        if (existing) {
          existing.duplicates.push(entry.problem);
        } else {
          problemStatus.set(alphabet, entry);
        }
      });

      const rowColor = combineTableColorList({
        colorMode,
        colorList: problemStatusList.map(({ cellColor }) => cellColor),
      });
      const attempted = problemStatusList.some(
        ({ status }) => status.label !== StatusLabel.None
      );
      return {
        contest,
        id: contest.id,
        problemStatus,
        attempted,
        rowColor,
      } as OneContest;
    })
    .filter(({ problemStatus }: OneContest) => problemStatus.size > 0)
    .filter(
      ({ attempted }: OneContest) =>
        !props.showOnlyAttemptedContest || attempted
    )
    .filter(
      ({ problemStatus }: OneContest) =>
        !props.hideCompleteContests ||
        Array.from(problemStatus.values()).some(
          ({ status }) => status.label !== StatusLabel.Success
        )
    )
    .sort((a, b) => {
      const ax = a.contest.start_epoch_second;
      const bx = b.contest.start_epoch_second;
      if (ax === 0 && bx !== 0) return -1;
      if (ax !== 0 && bx === 0) return 1;
      return bx - ax;
    });

  const headerList = props.contests
    .flatMap((contest) => {
      const problems = props.contestToProblems.get(contest.id) ?? [];
      return problems.map((p) => getProblemHeaderAlphabet(p, contest));
    })
    .filter((alphabet) => alphabet.length > 0);

  const header = Array.from(new Set(headerList)).sort();

  return (
    <Row className="my-4">
      <h2>{props.title}</h2>
      <BootstrapTable
        data={contests}
        tableContainerClass="contest-table-responsive contest-regular-table-responsive"
      >
        <TableHeaderColumn
          isKey
          dataField="id"
          columnClassName={(_: string, { rowColor }: OneContest): TableColor =>
            rowColor
          }
          dataFormat={(_, { contest }: OneContest): React.ReactElement => (
            <ContestLink contest={contest} title={contest.id.toUpperCase()} />
          )}
        >
          Contest
        </TableHeaderColumn>
        {header.map((c) => (
          <TableHeaderColumn
            dataField={c}
            key={c}
            className={() => TableColor.None}
            columnClassName={(_, { problemStatus }: OneContest): string => {
              const problem = problemStatus.get(c);
              const hasDuplicate = !!problem && problem.duplicates.length > 0;
              return [
                "table-problem",
                !problem ? "table-problem-empty" : problem.cellColor,
                hasDuplicate ? "table-problem-duplicate" : "",
              ]
                .filter((nm) => nm)
                .join(" ");
            }}
            dataFormat={(
              _,
              { contest, problemStatus }: OneContest
            ): string | React.ReactElement => {
              const problem = problemStatus.get(c);
              const model = problem ? problem.model : undefined;
              if (problem) {
                const INF_POINT = 1e18;
                const point = problem.problem.point ?? INF_POINT;
                const duplicateTitle =
                  problem.duplicates.length > 0
                    ? `Column "${c}" has multiple problems collapsed into it. Shown: ${
                        problem.problem.problem_index
                      } (${
                        problem.problem.id
                      }). Hidden: ${problem.duplicates
                        .map((d) => `${d.problem_index} (${d.id})`)
                        .join(", ")}`
                    : undefined;
                return (
                  <>
                    {problem.duplicates.length > 0 && (
                      <span
                        title={duplicateTitle}
                        style={{
                          color: "#fff",
                          background: "#dc3545",
                          fontWeight: "bold",
                          borderRadius: "3px",
                          padding: "0 4px",
                          marginRight: "4px",
                          cursor: "help",
                        }}
                      >
                        ⚠ DUP x{problem.duplicates.length + 1}
                      </span>
                    )}
                    <ProblemLink
                      isExperimentalDifficulty={
                        !!model && model.is_experimental
                      }
                      showDifficultyUnavailable
                      showDifficulty={props.showDifficulty}
                      contestId={contest.id}
                      problemId={problem.problem.id}
                      problemIndex={problem.problem.problem_index}
                      problemName={problem.problem.name}
                      problemModel={model}
                      userRatingInfo={userRatingInfo}
                    />
                    {props.colorMode === ColorMode.None && (
                      <ProblemPoint point={point} />
                    )}
                    {props.colorMode === ColorMode.ContestResult && (
                      <SubmitTimespan
                        contest={contest}
                        problemStatus={problem.status}
                        showPenalties={showPenalties}
                      />
                    )}
                  </>
                );
              } else {
                return "";
              }
            }}
          >
            {c}
          </TableHeaderColumn>
        ))}
      </BootstrapTable>
    </Row>
  );
};

export const AtCoderRegularTable = React.memo(AtCoderRegularTableSFC);
